/**
 * @fileoverview Forked from https://github.com/pmndrs/zustand/blob/main/src/context.ts
 *
 * The author of zustand suggested in https://github.com/pmndrs/zustand/issues/837
 * that they are dropping zustand/context. We made our own version to keep
 * zustand working with React context.
 *
 * `Provider` now provides a zustand store instead of `createStore` method.
 * The original zustand context library needed `createStore` method to support
 * `initialStore` which is now deprecated. Replacing it with the store allows us
 * to access the store instance from outside of the provider scope, which is
 * useful for accessing store state in tests.
 *
 * zustand 5: bound store hooks no longer accept an equality function, so
 * useStore delegates to useStoreWithEqualityFn from zustand/traditional to
 * keep the (selector, equalityFn) API this fork has always exposed.
 */
import type { ReactNode } from 'react'
import {
  createContext as reactCreateContext,
  createElement,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { useStoreWithEqualityFn } from 'zustand/traditional'

import type {
  EqualityChecker,
  State,
  StateSelector,
  UseBoundStore,
} from './compat'

/**
 * @deprecated Use `typeof MyContext.useStore` instead.
 */
export type UseContextStore<T extends State> = {
  (): T
  <U>(selector: StateSelector<T, U>, equalityFn?: EqualityChecker<U>): U
}

/**
 * Stable fallback for `useStore()` calls without a selector. It must keep a
 * constant identity: useSyncExternalStoreWithSelector memoizes on the selector
 * reference, so a fresh function each render would re-subscribe every render.
 */
const identitySelector = (state: unknown) => state

/**
 *
 */
export function createContext<
  TState extends State,
  TUseBoundStore extends UseBoundStore<TState> = UseBoundStore<TState>,
>() {
  const ZustandContext = reactCreateContext<TUseBoundStore | undefined>(
    undefined,
  )

  const Provider = ({
    store,
    children,
  }: {
    store: TUseBoundStore
    children: ReactNode
  }) => {
    const storeRef = useRef<TUseBoundStore>(undefined)

    if (!storeRef.current) {
      storeRef.current = store
    }

    return createElement(
      ZustandContext.Provider,
      { value: storeRef.current },
      children,
    )
  }

  const useStore: UseContextStore<TState> = <StateSlice>(
    selector?: StateSelector<TState, StateSlice>,
    equalityFn?: EqualityChecker<StateSlice>,
  ) => {
    // ZustandContext value is guaranteed to be stable.
    const useProviderStore = useContext(ZustandContext)
    if (!useProviderStore) {
      throw new Error(
        'Seems like you have not used zustand provider as an ancestor.',
      )
    }
    return useStoreWithEqualityFn(
      useProviderStore,
      selector ?? (identitySelector as StateSelector<TState, StateSlice>),
      equalityFn,
    )
  }

  type StoreApi = {
    getState: TUseBoundStore['getState']
    getInitialState: TUseBoundStore['getInitialState']
    setState: TUseBoundStore['setState']
    subscribe: TUseBoundStore['subscribe']
  }

  const useStoreApiOptional = (): StoreApi | null => {
    const useProviderStore = useContext(ZustandContext)
    return useMemo(() => {
      if (!useProviderStore) {
        return null
      }
      return {
        getState: useProviderStore.getState,
        getInitialState: useProviderStore.getInitialState,
        setState: useProviderStore.setState,
        subscribe: useProviderStore.subscribe,
      }
    }, [useProviderStore])
  }

  const useStoreApi = (): StoreApi => {
    const api = useStoreApiOptional()
    if (!api) {
      throw new Error(
        'Seems like you have not used zustand provider as an ancestor.',
      )
    }
    return api
  }

  return {
    Provider,
    useStore,
    useStoreApi,
    useStoreApiOptional,
  }
}
